import { motion } from "framer-motion";
import { IMAGES } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const CHAPTERS = [
  {
    n: "01",
    title: "The Assessment",
    body: "Every home begins with a walkthrough. We note surfaces, finishes and priorities, then build a checklist that belongs to your space alone.",
    img: IMAGES.counter,
    alt: "Prepared pristine kitchen counter",
  },
  {
    n: "02",
    title: "Eco-Purity Standards",
    body: "Non-toxic, fume-free products as standard. Safe around children, pets and delicate stone — without compromising the shine.",
    img: IMAGES.sink,
    alt: "Pristine sink and soap detail",
  },
  {
    n: "03",
    title: "The Sparkle Audit",
    body: "A 40-point sign-off closes every visit. Taps polished to mirror, skirtings dusted, edges crisp — checked before we leave, not after.",
    img: IMAGES.faucet,
    alt: "Polished faucet detail",
  },
  {
    n: "04",
    title: "White-Glove Guarantee",
    body: "Not delighted? Tell us within 24 hours and we return to re-clean, free of charge. That is the standard our name stands on.",
    img: IMAGES.hero,
    alt: "Pristine living room after a clean",
  },
];

export default function Manifesto() {
  return (
    <section id="manifesto" data-testid="manifesto-section" className="py-24 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-20 max-w-2xl"
        >
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-[#1B523F]">
            Our Standard
          </p>
          <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-[#121A16] sm:text-5xl lg:text-6xl">
            A manifesto in
            <br />
            <em className="italic text-[#0F382A]">four chapters.</em>
          </h2>
        </motion.div>

        <div className="space-y-0">
          {CHAPTERS.map((c, i) => (
            <motion.div
              key={c.n}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: 0.05, ease: EASE }}
              data-testid={`manifesto-chapter-${c.n}`}
              className={`group grid grid-cols-1 items-center gap-10 border-t border-[#E5DEC3] py-14 lg:grid-cols-12 lg:py-20 ${
                i === CHAPTERS.length - 1 ? "border-b" : ""
              }`}
            >
              <div className={`lg:col-span-2 ${i % 2 ? "lg:order-3" : ""}`}>
                <span className="font-serif text-6xl font-light italic text-[#D4AF37]/70 transition-colors duration-500 group-hover:text-[#D4AF37] lg:text-7xl">
                  {c.n}
                </span>
              </div>
              <div className={`lg:col-span-6 ${i % 2 ? "lg:order-1" : ""}`}>
                <h3 className="font-serif text-3xl font-light text-[#121A16] lg:text-4xl">
                  {c.title}
                </h3>
                <p className="mt-5 max-w-lg text-base leading-relaxed text-[#4A5A52]">{c.body}</p>
              </div>
              <div className={`lg:col-span-4 ${i % 2 ? "lg:order-2" : ""}`}>
                <div className="overflow-hidden rounded-[1.5rem]">
                  <img
                    src={c.img}
                    alt={c.alt}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
